// src/pages/ImportPage.js
import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { parseExcel } from '../utils/excelUtils';
import { importCattleRecords } from '../utils/cattleService';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export default function ImportPage() {
  const { currentUser } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState('');
  const fileRef = useRef();
  const navigate = useNavigate();

  async function handleFileChange(e) {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    try {
      const records = await parseExcel(f);
      setPreview(records.slice(0, 5));
      toast.success(`Parsed ${records.length} records from file`);
    } catch (err) {
      toast.error('Failed to parse Excel file. Please check the format.');
    }
  }

  async function handleImport() {
    if (!file) return;
    setImporting(true);
    setProgress('Parsing file...');
    try {
      const records = await parseExcel(file);
      setProgress(`Importing ${records.length} records to database...`);
      const { success, skipped } = await importCattleRecords(currentUser.uid, records);
      
      let msg = `✅ Successfully imported ${success} cattle records!`;
      if (skipped.db > 0 || skipped.file > 0) {
        msg += ` (${skipped.db} already in DB, ${skipped.file} duplicates in file skipped)`;
      }
      toast.success(msg, { duration: 6000 });
      
      setProgress('');
      setFile(null);
      setPreview([]);
      if (fileRef.current) fileRef.current.value = '';
      navigate('/cattle');
    } catch (err) {
      toast.error('Import failed: ' + err.message);
      setProgress('');
    }
    setImporting(false);
  }

  return (
    <div className="page-container">
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1b5e20', marginBottom: 6 }}>📥 Import from Excel</h1>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
        Import your cattle records from the Excel file. The file should follow the standard Gaushala format with Hindi column headers.
      </p>

      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontWeight: 700, marginBottom: 12 }}>📋 Expected Excel Columns</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {['क्रम संख्या', 'आवक की दिनांक', 'टैग क्रमांक', 'टैग का रंग', 'नस्ल', 'आयु', 'नर/मादा', 'गौवंश का रंग', 'पहचान का चिन्ह', 'अंधा/विकलांग/बीमार', 'निकास की दिनांक', 'कारण'].map(col => (
            <span key={col} style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '3px 10px', fontSize: 13, color: '#15803d' }}>{col}</span>
          ))}
        </div>
      </div>

      <div className="card">
        <div
          style={{
            border: '2px dashed #d1d5db', borderRadius: 10, padding: 40, textAlign: 'center',
            cursor: 'pointer', marginBottom: 20, background: file ? '#f0fdf4' : 'white',
            borderColor: file ? '#86efac' : '#d1d5db',
          }}
          onClick={() => fileRef.current?.click()}
        >
          <div style={{ fontSize: 48, marginBottom: 12 }}>{file ? '✅' : '📁'}</div>
          <p style={{ fontWeight: 600, fontSize: 16 }}>
            {file ? file.name : 'Click to select Excel file (.xlsx)'}
          </p>
          <p style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
            {file ? `Size: ${(file.size / 1024).toFixed(1)} KB` : 'Supports .xlsx format'}
          </p>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: 'none' }} />
        </div>

        {preview.length > 0 && (
          <div>
            <h4 style={{ fontWeight: 700, marginBottom: 10, color: '#15803d' }}>Preview (first 5 rows):</h4>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Tag Number</th>
                    <th>Entry Date</th>
                    <th>Breed</th>
                    <th>Age</th>
                    <th>Gender</th>
                    <th>Color</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((r, i) => (
                    <tr key={i}>
                      <td>{r.tagNumber}</td>
                      <td>{r.dateOfEntry}</td>
                      <td>{r.breed}</td>
                      <td>{r.age}</td>
                      <td>{r.gender}</td>
                      <td>{r.color}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {progress && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 14, margin: '16px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div>
            <span style={{ fontSize: 14, color: '#1d4ed8' }}>{progress}</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn btn-primary" onClick={handleImport} disabled={!file || importing}>
            {importing ? '⏳ Importing...' : '📥 Import Records'}
          </button>
          {file && (
            <button className="btn btn-secondary" onClick={() => { setFile(null); setPreview([]); if (fileRef.current) fileRef.current.value = ''; }}>
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
